import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, Sparkles, RotateCcw } from "lucide-react";
import OnboardingShell from "./OnboardingShell";
import { api } from "@/lib/api";

const TARGETS = [
    { v: "call", l: "📞 Дзвонять" },
    { v: "form", l: "📋 Форма" },
    { v: "visit", l: "🚶 Особисто" },
    { v: "messenger", l: "💬 Месенджер" },
    { v: "online_purchase", l: "🛒 Онлайн покупка" },
    { v: "email", l: "✉️ Email" },
];

const YEARS = [
    { v: "<1", l: "< 1 року" },
    { v: "1-3", l: "1–3 роки" },
    { v: "3-7", l: "3–7 років" },
    { v: "7+", l: "7+ років" },
];

const PRICE_TIERS = [
    { v: "economy", l: "💰 Економ" },
    { v: "mid", l: "💎 Середній" },
    { v: "premium", l: "👑 Преміум" },
];

const TOTAL_STEPS = 6;

export default function Step2Business() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromStrategy = searchParams.get("from") === "strategy";
    const [data, setData] = useState({
        // Q1
        has_site: null, site_url: "",
        // Q2 extended
        name: "", niche: "", city: "",
        years_in_business: "", working_hours: "",
        service_radius: "", price_tier: "",
        target_action: [],
        // Q3 audience
        audience: "", client_questions: "", client_fears: "", usp: "",
        // Q4 competitors
        competitors: "", why_us_vs_them: "",
        // Q5 goals
        monthly_budget: "", desired_clients: "", ltv: "",
        // Q6 triggers
        search_timing: "", explicit_negatives: "",
        // freeform deep-dive
        extra_notes: "",
    });
    const [q, setQ] = useState(1);
    const [saving, setSaving] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [error, setError] = useState("");
    const [showSummary, setShowSummary] = useState(false);
    const [hasExistingStrategy, setHasExistingStrategy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/business");
                if (r.data && Object.keys(r.data).length) {
                    let ta = r.data.target_action;
                    if (typeof ta === "string") ta = [ta];
                    else if (!Array.isArray(ta)) ta = [];
                    setData((d) => ({
                        ...d,
                        ...r.data,
                        target_action: ta,
                        monthly_budget: r.data.monthly_budget ?? "",
                        desired_clients: r.data.desired_clients ?? "",
                        ltv: r.data.ltv ?? "",
                    }));
                }
                // Check if a strategy already exists (so we can show "regenerate" button)
                const s = await api.get("/strategy").catch(() => ({ data: null }));
                if (s.data?.status === "ready" && s.data?.strategy_json) {
                    setHasExistingStrategy(true);
                }
            } catch {}
        })();
    }, []);

    const save = async (override = {}) => {
        const payload = { ...data, ...override };
        const out = {
            ...payload,
            monthly_budget: payload.monthly_budget === "" ? null : Number(payload.monthly_budget),
            desired_clients: payload.desired_clients === "" ? null : parseInt(payload.desired_clients, 10),
            ltv: payload.ltv === "" ? null : Number(payload.ltv),
            site_url: payload.site_url || null,
            years_in_business: payload.years_in_business || null,
            working_hours: payload.working_hours || null,
            service_radius: payload.service_radius || null,
            price_tier: payload.price_tier || null,
            client_questions: payload.client_questions || null,
            client_fears: payload.client_fears || null,
            competitors: payload.competitors || null,
            why_us_vs_them: payload.why_us_vs_them || null,
            search_timing: payload.search_timing || null,
            explicit_negatives: payload.explicit_negatives || null,
            extra_notes: payload.extra_notes || null,
        };
        await api.put("/business", out);
    };

    const handleNext = async () => {
        setError("");
        if (q < TOTAL_STEPS) {
            try { await save(); setQ(q + 1); window.scrollTo(0, 0); }
            catch { setError("Помилка збереження"); }
            return;
        }
        // On the final step: save first, then for first-time strategy
        // generation show a summary modal so the user can review what AI
        // will receive before committing 2 minutes to generation.
        setSaving(true);
        try {
            await save();
            if (fromStrategy) {
                await api.delete("/strategy");
                navigate("/strategy?regenerate=1");
                return;
            }
            if (hasExistingStrategy) {
                // Returning user with strategy already — skip summary, go straight.
                await api.post("/onboarding/step", { step: "3" });
                navigate("/onboarding/step-3");
                return;
            }
            // First-time generation → open summary modal
            setShowSummary(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Помилка збереження");
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmGenerate = async () => {
        setShowSummary(false);
        setSaving(true);
        try {
            await api.post("/onboarding/step", { step: "3" });
            navigate("/onboarding/step-3");
        } catch (e) {
            setError(e?.response?.data?.detail || "Помилка переходу");
            setSaving(false);
        }
    };

    const handleRegenerate = async () => {
        setError("");
        setRegenerating(true);
        try {
            await save();
            // Drop the existing strategy so step-3 will regenerate from scratch.
            await api.delete("/strategy");
            if (fromStrategy) {
                navigate("/strategy?regenerate=1");
                return;
            }
            await api.post("/onboarding/step", { step: "3" });
            navigate("/onboarding/step-3");
        } catch (e) {
            setError(e?.response?.data?.detail || "Не вдалося скинути стратегію");
        } finally {
            setRegenerating(false);
        }
    };

    const handleBack = () => {
        if (q === 1) {
            navigate(fromStrategy ? "/strategy" : "/onboarding");
        } else { setQ(q - 1); window.scrollTo(0, 0); }
    };

    const toggleTarget = (v) => {
        setData((d) => {
            const cur = Array.isArray(d.target_action) ? d.target_action : [];
            return { ...d, target_action: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
        });
    };

    // Validation per question
    const v = {
        1: data.has_site !== null && (data.has_site === false || (data.has_site === true && data.site_url)),
        2: data.name && data.niche && data.city && data.years_in_business && data.price_tier && data.service_radius && Array.isArray(data.target_action) && data.target_action.length > 0,
        3: data.audience && data.usp,
        4: true, // competitors optional
        5: data.monthly_budget !== "" && data.ltv !== "" && data.desired_clients !== "",
        6: true, // triggers optional
    };
    const canNext = v[q];

    return (
        <OnboardingShell current={2}>
            <div className="max-w-[780px]">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h1 className="font-display font-extrabold text-text-primary" style={{ fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em" }}>
                        Розкажіть про ваш бізнес
                    </h1>
                    <span className="font-mono text-xs text-faint-c" data-testid="onb2-q-counter">Q{q}/{TOTAL_STEPS}</span>
                </div>
                <p className="mt-3 text-secondary-c">Чим точніше відповіді — тим точнішу стратегію згенерує AI. ~10 хвилин.</p>

                <div className="mt-5 h-1 bg-surface rounded-full overflow-hidden" data-testid="onb2-progress">
                    <div className="h-full bg-primary transition-all duration-500" style={{ width: `${(q / TOTAL_STEPS) * 100}%` }} />
                </div>

                <div className="mt-8 glass rounded-2xl p-7">
                    {q === 1 && <Q1 data={data} setData={setData} />}
                    {q === 2 && <Q2 data={data} setData={setData} toggleTarget={toggleTarget} />}
                    {q === 3 && <Q3 data={data} setData={setData} />}
                    {q === 4 && <Q4 data={data} setData={setData} />}
                    {q === 5 && <Q5 data={data} setData={setData} />}
                    {q === 6 && <Q6 data={data} setData={setData} />}

                    {error && <div className="mt-5 text-sm text-error-c font-mono" data-testid="onb2-error">{error}</div>}
                </div>

                <div className="mt-8 flex justify-between gap-3 flex-wrap">
                    <button onClick={handleBack} className="btn-ghost" data-testid="onb2-back-btn">
                        <ArrowLeft size={16} /> Назад
                    </button>
                    <div className="flex gap-3 flex-wrap">
                        {q === TOTAL_STEPS && hasExistingStrategy && (
                            <button onClick={handleRegenerate} disabled={regenerating || saving} data-testid="onb2-regenerate-btn"
                                className={`btn-ghost ${(regenerating || saving) ? "opacity-50 cursor-not-allowed" : ""}`}>
                                {regenerating && <Loader2 size={16} className="animate-spin" />}
                                <RotateCcw size={16} /> Генерувати заново
                            </button>
                        )}
                        <button onClick={handleNext} disabled={!canNext || saving || regenerating} data-testid="onb2-next-btn"
                            className={`btn-primary ${(!canNext || saving || regenerating) ? "opacity-50 cursor-not-allowed" : "pulse-glow"}`}>
                            {saving && <Loader2 size={16} className="animate-spin" />}
                            {q < TOTAL_STEPS ? (
                                <>Далі <ArrowRight size={16} /></>
                            ) : hasExistingStrategy ? (
                                <><ArrowRight size={16} /> До стратегії</>
                            ) : (
                                <><Sparkles size={16} /> Згенерувати стратегію</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            {showSummary && (
                <SummaryModal
                    data={data}
                    onClose={() => setShowSummary(false)}
                    onConfirm={handleConfirmGenerate}
                />
            )}
        </OnboardingShell>
    );
}

/* ─── Summary modal shown before AI generation ─── */
const SummaryModal = ({ data, onClose, onConfirm }) => {
    const trunc = (s, n) => !s ? "—" : (String(s).length > n ? String(s).slice(0, n) + "…" : String(s));
    const items = [
        { label: "Бізнес", value: trunc(data.name, 50) },
        { label: "Ніша", value: trunc(data.niche, 50) },
        { label: "Сайт", value: trunc(data.site_url, 50) },
        { label: "Місто / гео", value: trunc(data.city, 50) },
        { label: "Аудиторія", value: data.audience ? `${data.audience.length} симв.` : "—" },
        { label: "USP / переваги", value: data.usp ? `${data.usp.length} симв.` : "—" },
        { label: "Цільова дія", value: trunc(data.target_action, 50) },
        { label: "Цінова категорія", value: data.price_tier || "—" },
        { label: "Питання клієнтів", value: data.client_questions ? `${data.client_questions.length} симв.` : "—" },
        { label: "Страхи клієнтів", value: data.client_fears ? `${data.client_fears.length} симв.` : "—" },
        { label: "Конкуренти", value: data.competitors ? `${data.competitors.length} симв.` : "—" },
        { label: "Чому обирають вас", value: data.why_us_vs_them ? `${data.why_us_vs_them.length} симв.` : "—" },
        { label: "Бюджет / міс", value: data.monthly_budget ? `$${data.monthly_budget}` : "—" },
        { label: "Бажано клієнтів / міс", value: data.desired_clients || "—" },
        { label: "LTV клієнта", value: data.ltv ? `$${data.ltv}` : "—" },
        { label: "Коли шукають", value: data.search_timing ? `${data.search_timing.length} симв.` : "—" },
        { label: "Мінус-слова", value: data.explicit_negatives ? `${data.explicit_negatives.length} симв.` : "—" },
        { label: "Додатковий контекст", value: data.extra_notes ? `${data.extra_notes.length} симв.` : "—" },
    ];
    const textFields = ["name","niche","city","audience","usp","target_action",
        "client_questions","client_fears","competitors","why_us_vs_them",
        "search_timing","explicit_negatives","extra_notes"];
    const totalContext = textFields.reduce((sum, k) => sum + (data[k] || "").length, 0);

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="summary-modal">
            <div className="bg-surface border border-border rounded-xl max-w-[640px] w-full max-h-[90vh] overflow-y-auto p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3">
                    <span className="text-2xl">👀</span>
                    <h2 className="font-display font-bold text-xl text-text-primary">Перевіримо разом перед стартом</h2>
                </div>
                <p className="mt-2 text-sm text-secondary-c">AI отримає ці дані. Якщо щось забули — поверніться і допишіть, інакше доведеться регенерувати.</p>

                <div className="mt-5 divide-y divide-border border border-border rounded-lg overflow-hidden">
                    {items.map((it) => (
                        <div key={it.label} className="flex items-center justify-between px-4 py-2.5 text-sm">
                            <span className="text-secondary-c">{it.label}</span>
                            <span className="text-text-primary font-mono text-xs truncate max-w-[60%] text-right">{it.value}</span>
                        </div>
                    ))}
                </div>

                <div className="mt-4 text-xs text-faint-c text-center">
                    Всього контексту: <span className="text-text-primary font-mono">{totalContext} симв.</span> · Генерація триватиме ~2 хвилини.
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row justify-end gap-3">
                    <button onClick={onClose} className="btn-ghost" data-testid="summary-edit-btn">
                        <ArrowLeft size={16} /> Виправити
                    </button>
                    <button onClick={onConfirm} className="btn-primary pulse-glow" data-testid="summary-confirm-btn">
                        <Sparkles size={16} /> Так, поїхали!
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Question components ─── */

const Q1 = ({ data, setData }) => (
    <div data-testid="onb2-q1">
        <h2 className="font-display font-bold text-xl text-text-primary">Є у вас сайт?</h2>
        <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => setData({ ...data, has_site: true })} data-testid="q1-yes"
                className={`btn-ghost ${data.has_site === true ? "border-primary text-text-primary" : ""}`}>
                Так, є сайт
            </button>
            <button onClick={() => setData({ ...data, has_site: false, site_url: "" })} data-testid="q1-no"
                className={`btn-ghost ${data.has_site === false ? "border-primary text-text-primary" : ""}`}>
                Немає сайту
            </button>
        </div>
        {data.has_site === true && (
            <div className="mt-5">
                <Label>URL сайту</Label>
                <input type="url" value={data.site_url || ""}
                    onChange={(e) => setData({ ...data, site_url: e.target.value })}
                    placeholder="https://example.com" data-testid="q1-site-input"
                    className="mt-2 w-full bg-surface border border-border rounded-md px-4 py-3 text-text-primary outline-none focus:border-primary transition" />
            </div>
        )}
        {data.has_site === false && (
            <div className="mt-5 p-4 rounded-lg bg-surface-hover border border-border text-sm text-secondary-c" data-testid="q1-no-site-note">
                Окей. Налаштуємо рекламу під дзвінки / повідомлення. Пізніше допоможемо налаштувати Google Business Profile безкоштовно.
            </div>
        )}
    </div>
);

const Q2 = ({ data, setData, toggleTarget }) => (
    <div data-testid="onb2-q2">
        <h2 className="font-display font-bold text-xl text-text-primary">Основи бізнесу</h2>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Назва бізнесу" value={data.name} onChange={(v) => setData({ ...data, name: v })} testid="q2-name" />
            <Field label="Місто або регіон" value={data.city} onChange={(v) => setData({ ...data, city: v })} placeholder="напр. Краків" testid="q2-city" />
        </div>
        <Textarea label="Що продаєте / яку послугу надаєте" value={data.niche} onChange={(v) => setData({ ...data, niche: v })} hint="Опишіть конкретніше: 'ремонт айфонів і самсунгів з заміною скла за 30 хв'" testid="q2-niche" />

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <Label>Скільки років бізнесу?</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                    {YEARS.map((y) => (
                        <button key={y.v} onClick={() => setData({ ...data, years_in_business: y.v })}
                            data-testid={`q2-years-${y.v}`}
                            className={`px-3 py-1.5 rounded-full text-sm border transition ${data.years_in_business === y.v ? "border-primary text-text-primary bg-primary/10" : "border-border text-secondary-c hover:border-primary/40"}`}>
                            {y.l}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <Label>Цінова категорія</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                    {PRICE_TIERS.map((p) => (
                        <button key={p.v} onClick={() => setData({ ...data, price_tier: p.v })}
                            data-testid={`q2-price-${p.v}`}
                            className={`px-3 py-1.5 rounded-full text-sm border transition ${data.price_tier === p.v ? "border-primary text-text-primary bg-primary/10" : "border-border text-secondary-c hover:border-primary/40"}`}>
                            {p.l}
                        </button>
                    ))}
                </div>
            </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Графік роботи" value={data.working_hours} onChange={(v) => setData({ ...data, working_hours: v })} placeholder="9–18 пн-пт, 24/7, або інше" testid="q2-hours" optional />
            <Field label="Радіус обслуговування" value={data.service_radius} onChange={(v) => setData({ ...data, service_radius: v })} placeholder="центр міста / вся область / по країні" testid="q2-radius" />
        </div>

        <div className="mt-5">
            <Label>Як клієнти зв'язуються (можна обрати кілька)</Label>
            <div className="mt-2 flex flex-wrap gap-2">
                {TARGETS.map((t) => {
                    const active = Array.isArray(data.target_action) && data.target_action.includes(t.v);
                    return (
                        <button key={t.v} onClick={() => toggleTarget(t.v)} data-testid={`q2-target-${t.v}`}
                            className={`px-4 py-2 rounded-full text-sm border transition ${active ? "border-primary text-text-primary bg-primary/10" : "border-border text-secondary-c hover:border-primary/40"}`}>
                            {t.l}
                        </button>
                    );
                })}
            </div>
        </div>
    </div>
);

const Q3 = ({ data, setData }) => (
    <div data-testid="onb2-q3">
        <h2 className="font-display font-bold text-xl text-text-primary">Аудиторія і чим ви кращі</h2>
        <Textarea label="Опишіть типового клієнта" value={data.audience} onChange={(v) => setData({ ...data, audience: v })}
            hint="напр. 'Молоді сім'ї 28-40 років, з 1-2 дітьми, які щойно купили квартиру у Львові і потребують ремонту під ключ'"
            testid="q3-audience" />
        <Textarea label="Що клієнти зазвичай запитують перед покупкою?" value={data.client_questions} onChange={(v) => setData({ ...data, client_questions: v })}
            hint="напр. 'Скільки коштує? Як швидко зробите? Чи є гарантія? Чи можна в розстрочку?'"
            testid="q3-questions" optional />
        <Textarea label="Чого боїться клієнт?" value={data.client_fears} onChange={(v) => setData({ ...data, client_fears: v })}
            hint="напр. 'Боїться що пломба випаде через тиждень — тому ми даємо 2 роки гарантії'"
            testid="q3-fears" optional />
        <Textarea label="Чим відрізняєтесь від конкурентів?" value={data.usp} onChange={(v) => setData({ ...data, usp: v })}
            hint="Конкретно. Не 'якістю' — а 'виїзд за 2 години 24/7' або 'гарантія повернення грошей'"
            testid="q3-usp" />
    </div>
);

const Q4 = ({ data, setData }) => (
    <div data-testid="onb2-q4">
        <h2 className="font-display font-bold text-xl text-text-primary">Конкуренти (опційно)</h2>
        <p className="mt-2 text-sm text-secondary-c">Допоможе AI зрозуміти ринок і позиціонування.</p>
        <Textarea label="Назвіть 2-3 основних конкурентів" value={data.competitors} onChange={(v) => setData({ ...data, competitors: v })}
            hint="Назви компаній, URL сайтів, або просто 'мережа X і ще приватні майстри в районі'"
            testid="q4-competitors" optional />
        <Textarea label="Чому клієнти обирають вас, а не їх?" value={data.why_us_vs_them} onChange={(v) => setData({ ...data, why_us_vs_them: v })}
            hint="напр. 'У них дешевше але повільно і без гарантії. У нас на 20% дорожче але якісно і офіційно з договором'"
            testid="q4-why-us" optional />
    </div>
);

const Q5 = ({ data, setData }) => (
    <div data-testid="onb2-q5">
        <h2 className="font-display font-bold text-xl text-text-primary">Цілі і бюджет</h2>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Місячний бюджет ($)" type="number" value={data.monthly_budget} onChange={(v) => setData({ ...data, monthly_budget: v })} mono testid="q5-budget" />
            <Field label="Бажано клієнтів/міс" type="number" value={data.desired_clients} onChange={(v) => setData({ ...data, desired_clients: v })} mono testid="q5-clients" />
            <Field label="Скільки в середньому приносить один клієнт? ($)" type="number" hint="Сума × кількість покупок за весь час. Напр.: SaaS-підписка $30/міс × 6 міс = $180. Салон краси: $50 × 12 = $600. Інтернет-магазин: середній чек $80 × 3 покупки = $240." value={data.ltv} onChange={(v) => setData({ ...data, ltv: v })} mono testid="q5-ltv" />
        </div>
    </div>
);

const Q6 = ({ data, setData }) => {
    const notesLen = (data.extra_notes || "").length;
    return (
        <div data-testid="onb2-q6">
            <h2 className="font-display font-bold text-xl text-text-primary">Додатковий контекст</h2>
            <p className="mt-2 text-sm text-secondary-c">Чим більше деталей — тим точніша стратегія. Не лінуйтесь, AI читає все.</p>
            <Textarea label="Коли клієнти найчастіше шукають вашу послугу?" value={data.search_timing} onChange={(v) => setData({ ...data, search_timing: v })}
                hint="напр. 'Понеділок-середа вранці, коли власник аналізує тижневі продажі. Пік восени і перед різдвом, спад влітку.' — або просто 'цілий рік' якщо без сезону"
                testid="q6-timing" optional />
            <Textarea label="Які слова клієнти ТОЧНО НЕ шукають (мінус-слова від вас)?" value={data.explicit_negatives} onChange={(v) => setData({ ...data, explicit_negatives: v })}
                hint="напр. 'безкоштовно, своїми руками, відео, б/у, курси, вакансія' — все що приводить нецільових кліків"
                testid="q6-negatives" optional />
            <div className="mt-5">
                <Label>
                    Розкажіть більше про бізнес — все що ще не питали <span className="text-xs text-primary">(найважливіше поле!)</span>
                </Label>
                <textarea value={data.extra_notes || ""} onChange={(e) => setData({ ...data, extra_notes: e.target.value })}
                    rows={7} data-testid="q6-notes"
                    placeholder="Згадайте про сезонність, унікальність, болі клієнтів, географію, цінову позицію, що НЕ робите, історію бренду — все що допоможе AI зрозуміти ваш бізнес. Чим більше тим краще."
                    className="mt-2 w-full bg-surface border border-border rounded-md px-4 py-3 text-text-primary outline-none focus:border-primary transition resize-y" />
                <div className="mt-1 flex justify-between text-xs">
                    <span className="text-faint-c">Деталі: сезонність, унікальність, болі клієнтів, географія, цінова позиція, що НЕ робите.</span>
                    <span className={notesLen < 200 ? "text-faint-c" : notesLen < 500 ? "text-warning-c" : "text-success"}>
                        {notesLen} симв. {notesLen < 200 ? "(мінімум 200 для якісної стратегії)" : notesLen < 500 ? "(добре, але можна більше)" : "✓ відмінно"}
                    </span>
                </div>
            </div>
        </div>
    );
};

/* ─── Form primitives ─── */

const Label = ({ children }) => <label className="block text-sm text-secondary-c">{children}</label>;

const Field = ({ label, value, onChange, placeholder, type = "text", mono, hint, testid, optional }) => (
    <div>
        <Label>
            {label} {optional && <span className="text-xs text-faint-c">(опційно)</span>}
        </Label>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder} data-testid={testid}
            className={`mt-2 w-full bg-surface border border-border rounded-md px-4 py-3 text-text-primary outline-none focus:border-primary transition ${mono ? "font-mono" : ""}`} />
        {hint && <p className="mt-1 text-xs text-faint-c">{hint}</p>}
    </div>
);

const Textarea = ({ label, value, onChange, hint, testid, rows = 3, optional }) => (
    <div className="mt-5">
        <Label>
            {label} {optional && <span className="text-xs text-faint-c">(опційно)</span>}
        </Label>
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)}
            rows={rows} data-testid={testid}
            className="mt-2 w-full bg-surface border border-border rounded-md px-4 py-3 text-text-primary outline-none focus:border-primary transition resize-y" />
        {hint && <p className="mt-1 text-xs text-faint-c">{hint}</p>}
    </div>
);
